import {
  Icon,
  Link,
  Ryecon,
  RyeconHelp,
  RyeconStatusError,
  RyeconStatusReused,
  RyeconStatusSkipped,
  RyeconStatusSuccess,
  RyeconStatusWarning,
  Tooltip,
} from '@dbt-labs/sourdough';

import { RunStatus } from '@/__generated__/graphql';
import { useLinkToDbtCloud } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { CollapsibleSection } from '../CollapsibleSection';

export enum ResourceStatusResult {
  unknown = 0,
  pass = 1,
  warn = 2,
  error = 3,
  stale = 4,
  skipped = 5,
  reused = 6,
}

export const resourceStatusResultRyeconMap: Record<ResourceStatusResult, Ryecon> = {
  0: RyeconHelp,
  1: RyeconStatusSuccess,
  2: RyeconStatusWarning,
  3: RyeconStatusError,
  4: RyeconHelp,
  5: RyeconStatusSkipped,
  6: RyeconStatusReused,
};

export type ResourceStatus = RunStatus | string | null;

type ResourceStatusSectionParams = {
  header: string;
  lastRunId: number | null;
  shouldIndent: boolean;
  status: ResourceStatusResult;
  statusIcon: Ryecon;
  tooltip: React.ReactNode;
};

export const ResourceStatusSection = ({
  header,
  lastRunId,
  shouldIndent,
  status,
  statusIcon,
  tooltip,
}: ResourceStatusSectionParams) => {
  const projectScope = useUrlProjectParams();
  const projectIdFromUrl = projectScope.projectId;
  const getRunLink = useLinkToDbtCloud('runDetails');
  return (
    <CollapsibleSection
      disable={true}
      shouldIndent={shouldIndent}
      className="-mb-[1px] mt-1 flex w-full overflow-hidden border-b border-borderMuted p-4 text-fgMain"
      toExpand={undefined}
      closeAltText={''}
      expandAltText={''}
    >
      <span className="flex items-center" data-testid={`resource-status-${status}`}>
        <Tooltip content={tooltip} className="pointer-events-auto flex items-center">
          <Icon
            size="md"
            ryecon={statusIcon}
            className="pointer-events-auto align-middle"
          />
          <div className="sr-only">{tooltip}</div>
        </Tooltip>
      </span>
      <span>
        <p className="font-label-lg mx-2 align-middle">{header}</p>
      </span>
      <span className="flex-1 truncate text-right">
        {lastRunId && getRunLink && (
          <span>
            <Link
              isInternal={false}
              to={getRunLink({
                ...projectScope,
                projectId: projectIdFromUrl,
                runId: lastRunId,
              })}
              className="pointer-events-auto"
            >
              View run
            </Link>
          </span>
        )}
      </span>
    </CollapsibleSection>
  );
};
